import { type MainCurrencies } from '@capsicum/socios-core';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { type FieldError } from 'react-hook-form';

import { encode, type GetBalanceCurrencyDTO, useFormatter } from '../../../../../common';
import { ncwTransferAmountAtom } from '../data-access';

/**
 * This hook houses the shared code from useNCWSendChilizAmount() and useNCWSendTokenAmount();
 */
export const useNCWSendAmount = (
	tokenBalanceRaw?: GetBalanceCurrencyDTO,
	currencies?: MainCurrencies,
	hasDecimals?: boolean
) => {
	const { formatCrypto } = useFormatter();

	const [error, setError] = useState<FieldError | undefined>(undefined);

	const [transferAmount, setTransferAmount] = useAtom(ncwTransferAmountAtom);
	const initialValue = hasDecimals ? '0' : '1';

	useEffect(() => setTransferAmount(initialValue), []);

	// We really shouldn't be using floating point numbers for currency...
	// TODO: Get rid of these conversions
	const inputValue = Number(transferAmount ?? initialValue);
	const setInputValue = useCallback((newAmount: string) => {
		setTransferAmount(String(newAmount));
	}, []);

	const conversionAltPrice = encode('doubleTilde').concat(
		formatCrypto(Number(tokenBalanceRaw?.conversionRateFiat), currencies?.altCurrency?.shortCode ?? 'EUR')
	);

	const convertedDefaultPrice = useMemo(
		() =>
			encode('doubleTilde').concat(
				formatCrypto(
					inputValue * Number(tokenBalanceRaw?.conversionRateFiat || 0),
					currencies?.altCurrency?.shortCode ?? 'EUR'
				)
			),
		[inputValue, tokenBalanceRaw, currencies]
	);

	return {
		conversionAltPrice,
		convertedDefaultPrice,
		error,
		inputValue,
		setError,
		setInputValue
	};
};
